import { Button } from '@chopchop-se/ui';
import type { BarcodeConflictParams } from '@warehouse/shared';
import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { isApiRequestError } from '../../../lib/api-error';

/**
 * The anti-steal surface.
 *
 * When a bind collides, the API answers `E_CONFLICT` naming the article that
 * already owns the code (never a silent takeover — see
 * docs/recon/bc-inventory-stocktaking.md §2). This renders that as the only
 * two honest next steps: go look at the article that owns it, or bind a
 * different code. There is deliberately no "take it anyway" button here;
 * moving a barcode is an explicit edit on the barcode itself.
 */
export function barcodeConflictOf(error: unknown): BarcodeConflictParams | null {
  if (!isApiRequestError(error)) return null;
  const params = error.params;
  if (!params || params['reason'] !== 'barcode_taken') return null;
  return params as unknown as BarcodeConflictParams;
}

/**
 * The `reason` discriminator the API attaches to `E_CONFLICT` (`unit_in_use`,
 * `category_in_use`, `bc_synced`, `base_unit_locked`, …). Lets a page replace
 * the generic "Konflikt — posten har ändrats" with a sentence that actually
 * tells the operator what is in the way.
 */
export function conflictDetails(
  error: unknown,
): { reason: string; params: Record<string, unknown> } | null {
  if (!isApiRequestError(error) || error.code !== 'E_CONFLICT') return null;
  const params = error.params;
  const reason = params?.['reason'];
  return typeof reason === 'string' ? { reason, params: params ?? {} } : null;
}

export function BarcodeConflictNotice({
  conflict,
  onDismiss,
}: {
  conflict: BarcodeConflictParams;
  onDismiss?: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-xl border border-danger-600/30 bg-danger-600/[0.06] px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger-600" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">{t('masterData:barcodes.conflict.title')}</p>
          <p className="mt-1 text-sm text-ink-light">
            {t('masterData:barcodes.conflict.message', {
              barcode: conflict.barcode,
              articleNo: conflict.articleNo,
              articleName: conflict.articleName,
            })}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to={`/registers/articles/${conflict.articleId}`}>
          <Button size="sm" variant="secondary">
            {t('masterData:barcodes.conflict.open')}
          </Button>
        </Link>
        {onDismiss ? (
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            {t('common:actions.close')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
